/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React from "react";
import { useUI } from "../theme/ThemeProvider";

/**
 * Unified Card Component for admin_store
 * Provides consistent card styling across all pages
 * Matches admindashboard design style
 */
export default function UnifiedCard({
  children,
  icon: IconComponent,
  title,
  subtitle,
  badge,
  badgeColor,
  onClick,
  hoverable = true,
  className = "",
  value, // New prop for statistics cards
  valueColor, // Color for the value
  statCard = false, // New prop to enable statistics card layout
  ...props
}) {
  const { theme, lang } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";

  // Colors matching admindashboard design
  const colors = {
    cardBg: isDark ? "#1e293b" : "#ffffff",
    borderColor: isDark ? "#334155" : "#e2e8f0",
    textColor: isDark ? "#f1f5f9" : "#1e293b",
    subText: isDark ? "#94a3b8" : "#64748b",
    mainColor: isDark ? "#FFD700" : "#6A11CB",
    mainColorHover: isDark ? "#ffed4e" : "#5b0fb3",
    mainColorLight: isDark ? "#fef08a" : "#8b5cf6",
    dividerColor: isDark ? "#475569" : "#e5e7eb",
  };

  // Responsive padding matching admindashboard
  const padding = "p-4 sm:p-5 lg:p-6";

  return (
    <div
      className={`rounded-2xl shadow-sm ${padding} border transition-all duration-500 ease-out ${className} ${onClick ? "cursor-pointer active:scale-[0.98]" : ""
        }`}
      style={{
        backgroundColor: colors.cardBg,
        borderColor: colors.borderColor,
        borderWidth: "1px",
        width: "100%",
        ...(hoverable && {
          transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
        }),
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        if (hoverable) {
          e.currentTarget.style.transform = "translateY(-6px)";
          e.currentTarget.style.boxShadow = "0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 10px 10px -5px rgba(0, 0, 0, 0.02)";
          e.currentTarget.style.borderColor = colors.mainColor;
        }
      }}
      onMouseLeave={(e) => {
        if (hoverable) {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.05)";
          e.currentTarget.style.borderColor = colors.borderColor;
        }
      }}
      {...props}
    >
      {/* Statistics Card Layout - Icon next to title, value below centered */}
      {statCard ? (
        <>
          <div
            className="flex items-center gap-3 mb-4 sm:mb-5"
            style={{
              justifyContent: isArabic ? "flex-end" : "flex-start",
              flexDirection: isArabic ? "row-reverse" : "row",
            }}
          >
            {IconComponent && (
              <div
                className="flex-shrink-0 rounded-xl flex items-center justify-center shadow-sm"
                style={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: isDark ? "rgba(255, 215, 0, 0.1)" : "rgba(116, 0, 191, 0.08)",
                  color: colors.mainColor,
                }}
              >
                <IconComponent size={22} />
              </div>
            )}
            {title && (
              <h3
                className="font-bold tracking-tight"
                style={{
                  color: isDark ? "#CBD5E1" : "#475569",
                  fontSize: "clamp(0.75rem, 2.5vw, 0.875rem)",
                  margin: 0,
                  textTransform: "uppercase",
                }}
              >
                {title}
              </h3>
            )}
          </div>
          {(value !== undefined || children) && (
            <div className={isArabic ? "text-right" : "text-left"}>
              {value !== undefined && (
                <div
                  className="text-2xl sm:text-3xl font-black tracking-tighter"
                  style={{
                    color: valueColor || colors.textColor,
                    margin: 0,
                  }}
                >
                  {value}
                </div>
              )}
              {children && <div className="mt-3">{children}</div>}
            </div>
          )}
        </>
      ) : (
        <>
          {/* Regular Card Layout */}
          {(title || IconComponent || badge) && (
            <div className="flex justify-between items-start mb-4" style={{ gap: "12px" }}>
              {(title || IconComponent) && (
                <div className="flex items-center gap-3 flex-1" style={{ minWidth: 0 }}>
                  {IconComponent && (
                    <div
                      className="flex-shrink-0 rounded-xl flex items-center justify-center shadow-sm"
                      style={{
                        width: "36px",
                        height: "36px",
                        backgroundColor: isDark ? "rgba(255, 215, 0, 0.15)" : "rgba(116, 0, 191, 0.08)",
                        color: colors.mainColor,
                      }}
                    >
                      <IconComponent size={20} />
                    </div>
                  )}
                  {title && (
                    <div className="flex-1" style={{ minWidth: 0 }}>
                      <h3
                        className="font-extrabold tracking-tight"
                        style={{
                          color: colors.textColor,
                          fontSize: "clamp(0.95rem, 3vw, 1.15rem)",
                        }}
                      >
                        {title}
                      </h3>
                      {subtitle && (
                        <p
                          className="text-xs sm:text-sm mt-0.5 opacity-80"
                          style={{
                            color: colors.subText,
                          }}
                        >
                          {subtitle}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
              {badge && (
                <span
                  className="px-2.5 py-1 rounded-lg text-[10px] sm:text-xs font-bold flex-shrink-0 uppercase tracking-wider shadow-sm"
                  style={{
                    backgroundColor: badgeColor || colors.mainColorLight,
                    color: badgeColor ? "white" : colors.mainColor,
                  }}
                >
                  {badge}
                </span>
              )}
            </div>
          )}

          {children && (
            <>
              {(title || IconComponent || badge) && (
                <div
                  className="mb-4"
                  style={{
                    borderTop: `1px solid ${colors.dividerColor}`,
                    opacity: 0.5
                  }}
                />
              )}
              <div className="w-full overflow-hidden">{children}</div>
            </>
          )}
        </>
      )}
    </div>
  );
}

